import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, LogOut, LogIn, Menu, X, Gift } from 'lucide-react';
import { auth } from '../firebase/config';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import logo from '../assets/logo.svg';
import AuthModal from './AuthModal';
import AlertModal from './AlertModal';
import { useAlert } from '../hooks/UseAlert';
import UseUserData from '../hooks/UseUserData';

const Header = ({ services, onServiceSelect }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { alertConfig, closeAlert, success, warning, error, confirm } = useAlert();
  const { user, userData } = UseUserData();

  const isAdmin = user && (
    user.email === 'alaia.cristalbienetre@gmail.com' 
    //à décommenter si besoin
    /* || 
    user.email === 'contact@eclipsea.fr'
    */
  );
  
  const navigate = useNavigate();

  const handleBackToSite = () => {
    navigate('/');
  };

  // Fermer le menu mobile quand on sélectionne un service
  const handleServiceSelect = (service, category) => {
    onServiceSelect(service, category);
    setActiveMenu(null);
    setShowMobileMenu(false);
  };

  const toggleMenu = (category) => {
    setActiveMenu(activeMenu === category ? null : category);
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    confirm(
      'Êtes-vous sûr de vouloir vous déconnecter ?',
      async () => {
        try {
          await signOut(auth);
          setShowUserMenu(false);
          setShowMobileMenu(false);
          success('Vous êtes maintenant déconnecté.');
        } catch (err) {
          console.error('Erreur déconnexion:', err);
          error('Une erreur est survenue lors de la déconnexion. Veuillez réessayer.');
        }
      },
      'Confirmer la déconnexion'
    );
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          onAuthSuccess={handleAuthSuccess} 
        />
      )}

      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          {/* Header avec grille 3 colonnes pour centrer le logo */}
          <div className="grid grid-cols-3 items-center mb-4">
            {/* Colonne gauche : Menu hamburger (mobile) */}
            <div className="flex justify-start">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 hover:bg-purple-50 rounded-lg transition-colors"
              >
                {showMobileMenu ? (
                  <X className="w-6 h-6 text-purple-600" />
                ) : (
                  <Menu className="w-6 h-6 text-purple-600" />
                )}
              </button>
            </div>

            {/* Colonne centrale : Logo centré */}
            <div className="flex items-center justify-center">
              <img 
                src={logo} 
                alt="Alaia Cristal Bien-être" 
                className="w-24 h-16 lg:w-30 lg:h-20"
              />
            </div>

            {/* Colonne droite : Boutons (Cartes cadeaux + Connexion) */}
            <div className="flex items-center justify-end gap-2">
              {/* Bouton cartes cadeaux - Desktop uniquement */}
              <button
                onClick={() => navigate('/cartes-cadeaux')}
                className="hidden lg:flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                <Gift className="w-4 h-4" />
                Cartes Cadeaux
              </button>

              {/* Bouton connexion/déconnexion */}
              <div className="relative">
                {user ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <User className="w-5 h-5" />
                      <ChevronDown className={`w-4 h-4 transition-transform hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 min-w-56 z-50">
                        <div className="p-4 border-b border-gray-100">
                          <p className="font-medium text-purple-900 truncate">{userData?.name || user?.email}</p>
                        </div>

                        {isAdmin && (
                          <button
                            onClick={() => {
                              navigate('/admin/dashboard');
                              setShowUserMenu(false);
                              setShowMobileMenu(false);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-3 text-left text-purple-700 hover:bg-purple-50 transition-colors"
                          >
                            <User className="w-4 h-4" />
                            Accès administration
                          </button>
                        )}

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors rounded-b-lg"
                        >
                          <LogOut className="w-4 h-4" />
                          Se déconnecter
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <span className="hidden sm:inline">Connexion</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Titre */}
          <div className="text-center mb-6">
            <button 
              onClick={handleBackToSite} 
              className="text-2xl lg:text-3xl font-light text-purple-900 tracking-wide hover:text-purple-700 transition-colors"
            >
              Alaia Cristal Bien-être
            </button>
          </div>

          {/* Navigation Desktop */}
          <nav className="hidden lg:flex flex-wrap justify-center gap-6">
            {Object.keys(services).map((category) => (
              <div 
                key={category}
                className="relative"
              >
                <button 
                  onClick={() => toggleMenu(category)}
                  className="flex items-center gap-2 px-4 py-2 text-purple-800 hover:text-purple-600 transition-colors font-light"
                >
                  {category}
                  <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === category ? 'rotate-180' : ''}`} />
                </button>
                
                {activeMenu === category && (
                  <div className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-xl p-4 min-w-64 max-h-96 overflow-y-auto border border-gray-200 z-50">
                    {(Array.isArray(services[category])
                      ? services[category]
                      : services[category].items
                    ).map((service) => (
                      <button
                        key={service.id}
                        onClick={() => {
                          const categoryData = {
                            title: category,
                            info: !Array.isArray(services[category]) ? services[category].info : null,
                            complements: !Array.isArray(services[category]) ? services[category].complements : null
                          };
                          handleServiceSelect(service, categoryData);
                        }}
                        className="block w-full text-left px-4 py-3 hover:bg-purple-50 rounded-md transition-colors"
                      >
                        <div className="font-medium text-purple-900">{service.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Menu Mobile - Inchangé */}
        {showMobileMenu && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setShowMobileMenu(false)}
            ></div>

            <div className="fixed left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 lg:hidden overflow-y-auto shadow-2xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-light text-purple-900">Nos soins</h2>
                  <button
                    onClick={() => setShowMobileMenu(false)}
                    className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-purple-600" />
                  </button>
                </div>

                {/* Bouton Cartes Cadeaux mobile */}
                <button
                  onClick={() => {
                    navigate('/cartes-cadeaux');
                    setShowMobileMenu(false);
                  }}
                  className="w-full mb-6 flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Gift className="w-5 h-5" />
                  Cartes Cadeaux
                </button>

                {/* Catégories mobile */}
                <div className="space-y-2">
                  {Object.keys(services).map((category) => (
                    <div key={category} className="border-b border-gray-100 pb-2">
                      <button
                        onClick={() => toggleMenu(category)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left text-purple-800 hover:bg-purple-50 rounded-lg transition-colors font-medium"
                      >
                        {category}
                        <ChevronDown 
                          className={`w-5 h-5 transition-transform ${activeMenu === category ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      
                      {activeMenu === category && (
                        <div className="mt-2 ml-4 space-y-1">
                          {(Array.isArray(services[category])
                            ? services[category]
                            : services[category].items
                          ).map((service) => (
                            <button
                              key={service.id}
                              onClick={() => {
                                const categoryData = {
                                  title: category,
                                  info: !Array.isArray(services[category]) ? services[category].info : null,
                                  complements: !Array.isArray(services[category]) ? services[category].complements : null
                                };
                                handleServiceSelect(service, categoryData);
                              }}
                              className="block w-full text-left px-4 py-3 hover:bg-purple-50 rounded-md transition-colors text-sm"
                            >
                              <div className="text-purple-900">{service.name}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default Header;